import React from 'react';
import { Box, Chip, Typography, Button } from '@mui/material';
import { Lock } from '@mui/icons-material';
import type { Issue } from '../../../store/api/githubApi';
import { SEVERITY_COLOR } from '../constants';

// ── Locked issue placeholder ─────────────────────────────────────────────────
// Rendered instead of the normal row/card whenever issue.is_locked (see
// app.core.issue_gating on the backend — the data behind this is already
// redacted server-side, this component is purely the visual treatment, not
// a second layer of hiding). Mimics the outer card shape used elsewhere
// (border, 8px radius, 1 unit bottom margin, ~56px min height) so a locked
// row sits at the same rhythm as the unlocked rows around it, rather than
// looking like a foreign element. Genuinely blurs a placeholder bar (not
// real data — none was sent) so the row still reads as "something is here"
// instead of a blank gap.

const LockedIssueRow: React.FC<{ issue: Pick<Issue, 'severity'>; onClick?: () => void }> = ({ issue, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      position: 'relative',
      border: '1px solid', borderColor: 'grey.200', mb: 1, borderRadius: '8px',
      minHeight: 56, overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { borderColor: 'grey.400' } : undefined,
    }}
  >
    {/* Blurred placeholder content — real severity for a bit of color
       variety, everything else is filler, never real finding data. */}
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, minHeight: 56,
      filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none',
    }}>
      <Chip label={issue.severity} size="small" color={SEVERITY_COLOR[issue.severity] ?? 'default'}
        sx={{ textTransform: 'capitalize', fontWeight: 700, minWidth: 68, height: 24, fontSize: '0.75rem', flexShrink: 0 }} />
      <Box sx={{ height: 10, borderRadius: 1, bgcolor: 'grey.300', width: '45%' }} />
      <Box sx={{ height: 10, borderRadius: 1, bgcolor: 'grey.200', width: '20%', ml: 'auto' }} />
    </Box>

    {/* Unblurred overlay — the only thing a free user can actually read. */}
    <Box sx={{
      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.55)',
    }}>
      <Lock sx={{ fontSize: 16, color: 'text.secondary' }} />
      <Typography variant="body2" fontWeight={700} color="text.secondary">
        Upgrade to see this finding
      </Typography>
      {onClick && (
        <Button size="small" variant="outlined" sx={{ textTransform: 'none', ml: 1, py: 0.25 }} onClick={(e) => { e.stopPropagation(); onClick(); }}>
          Upgrade
        </Button>
      )}
    </Box>
  </Box>
);

export default LockedIssueRow;
